import React, { useCallback, useRef, useState } from 'react';

import {
  closestCenter,
  DndContext,
  DragOverlay,
  TouchSensor,
  useSensor,
  useSensors,
} from '@dnd-kit/core';
import type {
  CollisionDetection,
  DragEndEvent,
  DragStartEvent,
  UniqueIdentifier,
} from '@dnd-kit/core';
import {
  SortableContext,
  sortableKeyboardCoordinates,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import { IconButton, Typography } from '@mui/material';
import { noop } from 'lodash';
import { useSnackbar } from 'notistack';
import { animated, useSpring } from 'react-spring';
import { SkinnyPlusIcon } from 'src/components/icons/SkinnyPlusIcon';
import type { Apollo } from 'src/gql/generated';
import { useAlineMeasure } from 'src/hooks/dom-hooks';
import { makeStyles } from 'tss-react/mui';

import { getDefaultColumnColor } from './ColorPickerPopover';
import { ColumnItem, ColumnItemOverlay } from './ColumnItem';
import { DeleteColumnDialog } from './DeleteColumnDialog';
import type { Column } from './helpers';
import { getColumnDeletionBlockedMessage, getIsColumnLocked } from './helpers';
import { SortableColumnItem } from './SortableColumnItem';
import {
  SelectiveKeyboardSensor,
  SelectiveMouseSensor,
  SelectivePointerSensor,
} from '../utils/keyboardCoordinateGetter';

/**
 * Accessibility announcements for screen readers during column drag operations.
 * Includes position-aware messages (e.g. "position 2 of 5").
 */
function createColumnAccessibilityAnnouncements(columns: Column[]) {
  const getColumnName = (id: string | number) =>
    columns.find(c => c.id === String(id))?.name ?? 'column';

  const getPosition = (id: string | number) => {
    const index = columns.findIndex(c => c.id === String(id));
    return index >= 0 ? index + 1 : columns.length;
  };

  return {
    onDragCancel() {
      return 'Dragging cancelled. Column returned to original position.';
    },
    onDragEnd({ active, over }: DragEndEvent) {
      if (!over) return 'Column dropped outside of the list.';
      const columnName = getColumnName(active.id);
      const position = getPosition(over.id);
      return `${columnName} was dropped at position ${position} of ${columns.length}.`;
    },
    onDragOver({ over }: { over: { id: string | number } | null }) {
      if (!over) return;
      const position = getPosition(over.id);
      return `Over position ${position} of ${columns.length}.`;
    },
    onDragStart({ active }: DragStartEvent) {
      const columnName = getColumnName(active.id);
      const position = getPosition(active.id);
      return `Picked up ${columnName} column at position ${position} of ${columns.length}. Use arrow keys to reorder.`;
    },
  };
}

const useStyles = makeStyles()(theme => ({
  addButton: {
    color: theme.palette.text.secondary,
  },
  columns: {
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing(0.5),
    marginTop: theme.spacing(1),
  },
  emptyStateText: {
    color: theme.palette.text.secondary,
    fontSize: theme.typography.pxToRem(14),
    padding: theme.spacing(1.5),
    textAlign: 'center',
  },
  header: {
    alignItems: 'center',
    backgroundColor: theme.appColors.fieldHeader,
    borderRadius: theme.shape.borderRadius,
    display: 'flex',
    justifyContent: 'space-between',
    minHeight: 36,
    padding: theme.spacing(0.25, 0.75, 0.25, 1.5),
  },
  headerActions: {
    alignItems: 'center',
    display: 'flex',
    gap: theme.spacing(1.5),
  },
  insertedRow: {
    overflow: 'hidden',
  },
  root: {
    '&:last-of-type': {
      marginBottom: 0,
    },
    marginBottom: theme.spacing(2),
  },
  title: {
    color: theme.palette.text.secondary,
    fontWeight: 600,
  },
}));

interface ColumnCategoryGroupProps {
  /** All columns across every category on the board (for move-on-delete picker) */
  allColumns: Column[];
  category: Apollo.ColumnCategory;
  categoryLabel: string;
  columns: Column[];
  onAddColumn: (column: Omit<Column, 'id' | 'sortKey'>) => Promise<void>;
  onDeleteColumn: (columnId: string, targetColumnId: string) => void;
  onMoveColumn: (columnId: string, targetColumnId: string) => void;
  onUpdateColumn: (columnId: string, props: Partial<Omit<Column, 'id'>>) => void;
  /** Board's Signing singleton, whose row is not editable */
  signingColumnId: UUID | null;
  /** Whether adding new columns is allowed for this category */
  allowAdd?: boolean;
  /** Whether dragging/reordering is allowed for this category */
  allowDrag?: boolean;
  /** Rendered in the category header, left of the add button */
  headerAction?: React.ReactNode;
  /** Override the standard editable-row add flow (used by board-managed columns). */
  onAdd?: () => void | Promise<void>;
}

export function ColumnCategoryGroup({
  allColumns,
  allowAdd = true,
  allowDrag = true,
  category,
  categoryLabel,
  columns,
  headerAction,
  onAdd,
  onAddColumn,
  onDeleteColumn,
  onMoveColumn,
  onUpdateColumn,
  signingColumnId,
}: ColumnCategoryGroupProps): JSX.Element {
  const { classes } = useStyles();
  const { enqueueSnackbar } = useSnackbar();
  const [isAddingNew, setIsAddingNew] = useState(false);
  const [deleteColumn, setDeleteColumn] = useState<Column | null>(null);

  const handleRequestDelete = useCallback(
    (column: Column) => {
      const blockedMessage = getColumnDeletionBlockedMessage({
        allColumns,
        column,
        signingColumnId,
      });
      if (blockedMessage) {
        enqueueSnackbar(blockedMessage, { variant: 'warning' });
        return;
      }

      setDeleteColumn(column);
    },
    [allColumns, enqueueSnackbar, signingColumnId]
  );

  return (
    <div className={classes.root}>
      <CategoryHeader
        allowAdd={allowAdd}
        categoryLabel={categoryLabel}
        headerAction={headerAction}
        onAdd={onAdd ?? (() => setIsAddingNew(true))}
      />
      <DraggableColumnList
        allowDrag={allowDrag}
        category={category}
        columns={columns}
        isAddingNew={isAddingNew}
        signingColumnId={signingColumnId}
        onAddColumn={onAddColumn}
        onCancelAdd={() => setIsAddingNew(false)}
        onMoveColumn={onMoveColumn}
        onRequestDelete={handleRequestDelete}
        onUpdateColumn={onUpdateColumn}
      />
      {deleteColumn && (
        <DeleteColumnDialog
          key={deleteColumn.id}
          allColumns={allColumns}
          column={deleteColumn}
          signingColumnId={signingColumnId}
          onClose={() => setDeleteColumn(null)}
          onConfirm={onDeleteColumn}
        />
      )}
    </div>
  );
}

interface CategoryHeaderProps {
  allowAdd: boolean;
  categoryLabel: string;
  onAdd: () => void | Promise<void>;
  headerAction?: React.ReactNode;
}

function CategoryHeader({ allowAdd, categoryLabel, headerAction, onAdd }: CategoryHeaderProps) {
  const { classes } = useStyles();

  return (
    <div className={classes.header}>
      <Typography className={classes.title} variant="subtitle2">
        {categoryLabel}
      </Typography>
      <div className={classes.headerActions}>
        {headerAction}
        {allowAdd && (
          <IconButton className={classes.addButton} size="small" onClick={onAdd}>
            <SkinnyPlusIcon />
          </IconButton>
        )}
      </div>
    </div>
  );
}

/**
 * Expands a freshly inserted row into place instead of having it pop in.
 * Stops constraining the height once settled so the row can grow (e.g. an
 * error message) and so the drag handle in the left gutter isn't clipped.
 */
function InsertedRow({ children }: { children: React.ReactNode }) {
  const { classes } = useStyles();
  const [hasSettled, setHasSettled] = useState(false);
  const [ref, measurements] = useAlineMeasure<HTMLDivElement>();

  const style = useSpring({
    config: { clamp: true, friction: 26, tension: 320 },
    from: { maxHeight: 0, opacity: 0, transform: 'translateY(-4px)' },
    onRest: () => setHasSettled(true),
    // Hold at the collapsed state until the row has been measured.
    pause: !measurements,
    to: { maxHeight: measurements?.scrollHeight ?? 0, opacity: 1, transform: 'translateY(0px)' },
  });

  return (
    <animated.div
      ref={ref}
      className={hasSettled ? undefined : classes.insertedRow}
      style={hasSettled ? undefined : style}
    >
      {children}
    </animated.div>
  );
}

interface DraggableColumnListProps {
  allowDrag: boolean;
  category: Apollo.ColumnCategory;
  columns: Column[];
  isAddingNew: boolean;
  onAddColumn: (column: Omit<Column, 'id' | 'sortKey'>) => Promise<void>;
  onCancelAdd: () => void;
  onMoveColumn: (columnId: string, targetColumnId: string) => void;
  onRequestDelete: (column: Column) => void;
  onUpdateColumn: (columnId: string, props: Partial<Omit<Column, 'id'>>) => void;
  signingColumnId: string | null;
}

function DraggableColumnList({
  allowDrag,
  category,
  columns,
  isAddingNew,
  onAddColumn,
  onCancelAdd,
  onMoveColumn,
  onRequestDelete,
  onUpdateColumn,
  signingColumnId,
}: DraggableColumnListProps) {
  const { classes } = useStyles();
  const [activeId, setActiveId] = useState<string | null>(null);
  const lastOverIdRef = useRef<UniqueIdentifier | null>(null);

  const activeColumn = activeId ? columns.find(c => c.id === activeId) : null;
  const activeColumnIndex = activeId ? columns.findIndex(c => c.id === activeId) + 1 : 0;

  const sensors = useSensors(
    useSensor(SelectivePointerSensor, {
      activationConstraint: { distance: 8 },
    }),
    useSensor(TouchSensor, {
      activationConstraint: { delay: 250, tolerance: 5 },
    }),
    useSensor(SelectiveKeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    }),
    useSensor(SelectiveMouseSensor, {
      activationConstraint: { distance: 10 },
    })
  );

  /**
   * Collision detection with stability: remembers last valid target
   * to prevent flickering during fast drags.
   */
  const collisionDetection: CollisionDetection = useCallback(args => {
    const centerCollisions = closestCenter(args);
    if (centerCollisions.length > 0) {
      lastOverIdRef.current = centerCollisions[0]?.id ?? null;
      return centerCollisions;
    }
    return lastOverIdRef.current ? [{ id: lastOverIdRef.current }] : [];
  }, []);

  return (
    <DndContext
      accessibility={{
        announcements: createColumnAccessibilityAnnouncements(columns),
        screenReaderInstructions: {
          draggable:
            'Press tab to navigate to a column. Once focused, press space or enter to pick it up. Use up and down arrow keys to reorder. Press space or enter to drop, or escape to cancel.',
        },
      }}
      collisionDetection={collisionDetection}
      sensors={sensors}
      onDragCancel={() => setActiveId(null)}
      onDragEnd={event => {
        setActiveId(null);
        const { active, over } = event;
        if (!over || active.id === over.id) return;

        onMoveColumn(String(active.id), String(over.id));
      }}
      onDragStart={({ active }) => setActiveId(String(active.id))}
    >
      <SortableContext items={columns.map(c => c.id)} strategy={verticalListSortingStrategy}>
        <div className={classes.columns}>
          {columns.length === 0 && !isAddingNew && (
            <Typography className={classes.emptyStateText}>Click + to add a status</Typography>
          )}
          {columns.map((column, idx) => (
            <SortableColumnItem
              key={column.id}
              column={column}
              columnIndex={idx + 1}
              isDragDisabled={!allowDrag}
              isLocked={getIsColumnLocked(column, signingColumnId)}
              totalColumns={columns.length}
              onCancel={noop}
              onDelete={() => onRequestDelete(column)}
              onSave={columnData => {
                const { id, ...props } = columnData;
                if (id) onUpdateColumn(id, props);
              }}
            />
          ))}
          {isAddingNew && (
            <InsertedRow>
              <ColumnItem
                key="new-column"
                category={category}
                column={null}
                columnIndex={columns.length + 1}
                isEditing={true}
                totalColumns={columns.length + 1}
                onCancel={onCancelAdd}
                onSave={async columnData => {
                  await onAddColumn({
                    category,
                    color: columnData.color || getDefaultColumnColor(category),
                    name: columnData.name || '',
                  });
                  onCancelAdd();
                }}
              />
            </InsertedRow>
          )}
        </div>
      </SortableContext>
      <DragOverlay dropAnimation={null}>
        {activeColumn && (
          <ColumnItemOverlay
            column={activeColumn}
            columnIndex={activeColumnIndex}
            totalColumns={columns.length}
          />
        )}
      </DragOverlay>
    </DndContext>
  );
}
